import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getCurrentUser, User, getTenant } from '../services/api';
import AmbulanceStatusSyncRunner from '../components/AmbulanceStatusSyncRunner';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string, user: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isSystemAdmin: boolean;
  isTenantAdmin: boolean;
  /** Separate tenant-level Admin login (not Staff, not primary tenantAdmin) */
  isTenantLevelAdmin: boolean;
  /** False for tenant-level Admin logins — Settings Tenant tab is view-only */
  canModifyTenantSettings: boolean;
  hasPermission: (permission: string) => boolean;
  loading: boolean;
  displayName: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const justLoggedInRef = useRef(false);
  const loginTimeRef = useRef<number | null>(null);
  
  // Display name cached to prevent flickering on route changes
  const [displayName, setDisplayName] = useState<string>(() => {
    try {
      return localStorage.getItem('cachedDisplayName') || 'Ambulance Tracking';
    } catch {
      return 'Ambulance Tracking';
    }
  });

  useEffect(() => {
    const initializeAuth = async () => {
      // Load from localStorage first (for immediate UI)
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          
          // Check if we just logged in (within last 5 seconds) - don't call getCurrentUser
          const timeSinceLogin = loginTimeRef.current ? Date.now() - loginTimeRef.current : Infinity;
          const recentlyLoggedIn = justLoggedInRef.current || timeSinceLogin < 5000;
          
          if (!recentlyLoggedIn) {
            // Small delay to ensure token is set in axios interceptor
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Fetch latest user data from API to get updated permissions
            try {
              const response = await getCurrentUser();
             if (response.data.success && response.data.data) {
                const latestUser = response.data.data;
                setUser(latestUser);
                // Update localStorage with latest user data
                localStorage.setItem('user', JSON.stringify(latestUser));
              }
            } catch (apiError: any) {
              // Only clear auth on 401 (unauthorized) - token is invalid
              // But only if we're not in the middle of a login (avoid race conditions)
              const is401 = apiError.response?.status === 401;
              const recentlyLoggedIn = justLoggedInRef.current || (loginTimeRef.current && Date.now() - loginTimeRef.current < 5000);
              
              console.error('[AuthContext] getCurrentUser() error:', {
                status: apiError.response?.status,
                url: apiError.config?.url,
                message: apiError.message,
                is401,
                recentlyLoggedIn,
                justLoggedIn: justLoggedInRef.current,
                loginTime: loginTimeRef.current
              });
              
              if (is401 && !recentlyLoggedIn) {
                console.warn('[AuthContext] Token expired or invalid, clearing auth');
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                setToken(null);
                setUser(null);
              } else {
                // Don't clear auth - keep the user logged in with cached data
              }
            }
          } else {
            // If we just logged in, reset the flag after a delay
            setTimeout(() => {
              justLoggedInRef.current = false;
            }, 5000);
          }
        } catch (error) {
          console.error('[AuthContext] Error parsing stored user:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = (newToken: string, newUser: User) => {
    justLoggedInRef.current = true;
    loginTimeRef.current = Date.now();
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('cachedDisplayName');
    setDisplayName('Ambulance Tracking');
  };
  
  // Fetch and cache display name - runs once when user is available
  useEffect(() => {
    const fetchDisplayName = async () => {
      if (!user) return;
      
      // System admin
      if (user.isSystem) {
        const name = 'System Admin';
        setDisplayName(name);
        localStorage.setItem('cachedDisplayName', name);
        return;
      }
      
      // Try to get tenant hospital name
      const hasTenantAdminRole =
        user.type === 'tenant_level_admin' ||
        user.type === 'tenant_admin' ||
        user.roles?.includes('tenantAdmin') ||
        user.role === 'tenantAdmin';
      if (user.tenantId && (user.isSystem || hasTenantAdminRole)) {
        try {
          const response = await getTenant(user.tenantId);
          if (response.data.success && response.data.data?.spec?.hospital?.name) {
            const name = response.data.data.spec.hospital.name;
            setDisplayName(name);
            localStorage.setItem('cachedDisplayName', name);
            return;
          }
        } catch {
          // Fall through to tenantName
        }
      }
      
      // Use tenantName from user
      if (user.tenantName) {
        setDisplayName(user.tenantName);
        localStorage.setItem('cachedDisplayName', user.tenantName);
      }
    };
    
    fetchDisplayName();
  }, [user?.tenantId, user?.isSystem, user?.tenantName]);

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    if (user.isSystem) return true; // System admin has all permissions

    // Tenant-level Admin logins: full access except tenant settings writes
    if (user.type === 'tenant_level_admin') {
      if (
        permission === 'tenant.create' ||
        permission === 'tenant.update' ||
        permission === 'tenant.delete'
      ) {
        return false;
      }
      return true;
    }
    
    // Tenant admin has full access (bypass permission checks)
    const hasTenantAdminRole = (user.roles?.includes('tenantAdmin') || user.role === 'tenantAdmin');
    if (hasTenantAdminRole) return true;
    
    // Check if user has the permission by permStr (permission string)
    // Permissions are stored as { id: string, name: string }
    // where name is the permStr (e.g., "user.create", "booking.list")
    // Also check id field for backward compatibility
    return user.permissions?.some((p) => p.name === permission || p.id === permission) || false;
  };

  const isTenantLevelAdmin = user?.type === 'tenant_level_admin';
  const isTenantAdmin =
    isTenantLevelAdmin ||
    (user?.roles?.includes('tenantAdmin') || user?.role === 'tenantAdmin') ||
    false;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        isAuthenticated: !!token && !!user,
        isSystemAdmin: user?.isSystem || false,
        isTenantAdmin,
        isTenantLevelAdmin: !!isTenantLevelAdmin,
        canModifyTenantSettings:
          !!user &&
          !isTenantLevelAdmin &&
          (user.isSystem ||
            isTenantAdmin ||
            !!user.permissions?.some(
              (p) => p.name === 'tenant.update' || p.id === 'tenant.update'
            )),
        hasPermission,
        loading,
        displayName,
      }}
    >
      <AmbulanceStatusSyncRunner />
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

